import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { SalesDataTimePeriod } from "../../data-store";
import { UserInfoProvider } from "../../data-store/useUserInfo";
import useAvailableImpactCategories from "../../services/useOrganizationFeatures";
import { formatTimePeriod } from "../../util/timePeriodFormatting";
import OrganizationProvider from "../organizations/OrganizationProvider";
import Page from "../Page";
import { usePages } from "../pages";
import ImpactCategoryToggle from "../recipes/ImpactCategoryToggle";
import { SecondaryButtonLink } from "../utils/Button";
import { Upload } from "../utils/Vectors";
import "./SalesDashboardPage.css";
import "../Page.css";
import ChartsForTimePeriod from "./ChartsForTimePeriod";
import ChartsOverTime from "./ChartsOverTime";
import {
  ImpactCategoryProvider,
  useImpactCategory,
} from "./ImpactCategoryProvider";

const tracking = { pageName: "salesDashboard" };

interface TimePeriodDisplayProps {
  selectedTimePeriod: SalesDataTimePeriod | null;
}

function TimePeriodDisplay({ selectedTimePeriod }: TimePeriodDisplayProps) {
  const intl = useIntl();

  if (!selectedTimePeriod) {
    return (
      <div className="SalesDataTimePeriod">
        {intl.formatMessage({
          id: "components/sales-dashboard/SalesDashboardPage:allTime",
          defaultMessage: "All time",
        })}
      </div>
    );
  }

  const formattedPeriod = formatTimePeriod(selectedTimePeriod, intl);

  return <div className="SalesDataTimePeriod">{formattedPeriod}</div>;
}

export default function SalesDashboardPage() {
  const pages = usePages();
  const [selectedTimePeriod, setSelectedTimePeriod] =
    useState<SalesDataTimePeriod | null>(null);

  const handleTimePeriodChange = (period: SalesDataTimePeriod | null) => {
    setSelectedTimePeriod(period);
  };

  return (
    <Page contentHasPadding={false} tracking={tracking}>
      <UserInfoProvider>
        <OrganizationProvider>
          <ImpactCategoryProvider>
            <div className="StickyPageTitle SalesDashboardPage_Header">
              <Page.Title
                breadcrumb={pages.SalesDashboard.breadcrumb()}
                title={pages.SalesDashboard.title}
                inlineComponentEnd={
                  <div className="d-flex" style={{ gap: 16 }}>
                    <SalesDashboardPageUploadButton />
                  </div>
                }
                rightAlignEndComponent={true}
              />
              <div
                className="d-flex justify-content-between w-100"
                style={{ padding: "16px 0 0 0" }}
              >
                <div className="d-flex flex-column">
                  <div style={{ padding: "9px 0 0 0" }}>
                    {/* 8 (padding of button) + 1 (border of button) to make in line with impact toggle */}
                    <TimePeriodDisplay
                      selectedTimePeriod={selectedTimePeriod}
                    />
                  </div>
                </div>
                <div
                  className="d-flex flex-column align-items-end"
                  style={{ gap: 16 }}
                >
                  <SalesDashboardPageImpactToggle />
                </div>
              </div>
            </div>
            <SalesDashboardPageContent
              selectedTimePeriod={selectedTimePeriod}
              onTimePeriodChange={handleTimePeriodChange}
            />
          </ImpactCategoryProvider>
        </OrganizationProvider>
      </UserInfoProvider>
    </Page>
  );
}

export function SalesDashboardPageUploadButton() {
  const pages = usePages();

  return (
    <SecondaryButtonLink
      to={pages.SalesDataUpload.url}
      icon={<Upload width={16} />}
    >
      <FormattedMessage
        id="components/sales-dashboard/SalesDashboardPage:uploadSalesData"
        defaultMessage="Upload sales"
      />
    </SecondaryButtonLink>
  );
}

export function SalesDashboardPageImpactToggle() {
  const { impactCategory, setImpactCategory } = useImpactCategory();
  const availableImpactCategories = useAvailableImpactCategories();

  return (
    <ImpactCategoryToggle
      onChange={setImpactCategory}
      options={availableImpactCategories}
      selectedImpactCategory={impactCategory}
    />
  );
}

export function SalesDashboardPageControls() {
  const { impactCategory, setImpactCategory } = useImpactCategory();
  const pages = usePages();
  const availableImpactCategories = useAvailableImpactCategories();

  const impactCategoryToggle = (
    <ImpactCategoryToggle
      onChange={setImpactCategory}
      options={availableImpactCategories}
      selectedImpactCategory={impactCategory}
    />
  );

  return (
    <div className="SalesDashboardPageControls">
      <SecondaryButtonLink
        to={pages.SalesDataUpload.url}
        icon={<Upload width={16} />}
      >
        <FormattedMessage
          id="components/sales-dashboard/SalesDashboardPage:uploadSalesData"
          defaultMessage="Upload sales"
        />
      </SecondaryButtonLink>
      {impactCategoryToggle}
    </div>
  );
}

export function SalesDashboardPageContent({
  selectedTimePeriod,
  onTimePeriodChange,
}: {
  selectedTimePeriod: SalesDataTimePeriod | null;
  onTimePeriodChange: (period: SalesDataTimePeriod | null) => void;
}) {
  return (
    <div className="SalesDashboardPageContent">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <ChartsOverTime />
        <ChartsForTimePeriod
          selectedTimePeriod={selectedTimePeriod}
          onTimePeriodChange={onTimePeriodChange}
        />
      </div>
    </div>
  );
}
